/**
 * Transforms ConditionBuilder form data into DataAccessPolicyModel payloads
 * for the MCP batch policy create/update API.
 *
 * Follows the same pattern as generateColumnRules in
 * src/pages/Accounts/Details/DomainPolicy/Form/utils/prepreDataAccessPolicy.ts
 */
import {
  type DataAccessPolicyModel,
  type DataAccessPolicyRuleColumnsModel,
  EnumDataAccessPolicyModelPolicySubtype,
  EnumDataAccessPolicyModelResourceSourceType,
} from "@/api";
import {
  type ConditionEntry,
  isConditionGroup,
  type SerializedGroup,
  serializeConditionTree,
} from "@/components/ConditionBuilder";
import { warn } from "@/helpers/logger";
import { type RegisterMcpFormValues } from "@/pages/MCPRegistry/mcpServerDialogTypes";
import { countResourceConditions } from "@/pages/MCPRegistry/resourcePolicies/summarize";
import { type ResourcePolicyDraft } from "@/pages/MCPRegistry/resourcePolicies/types";
import { OPERATOR_TO_CONDITION_ID } from "./operatorMappings";

// ─── Constants ────────────────────────────────────────────────────────────────

/** Static data type used for all MCP policy column rules (per spec). */
const ELEMENT_DATA_TYPE = "VARCHAR(16777216)";

/** Hard-coded policy type ID for MCP resource access policies (per spec). */
const POLICY_DATA_TYPE_ID = "10";

/** The "Everyone" principal context — used for all MCP policies. */
const QUERY_CONTEXT_ID = "8";
const QUERY_CONTEXT_VALUE = "Everyone";

/**
 * "CONTENT" identifies a resource-level (document/content) access policy for
 * the Resource Policies wizard step. It does not yet exist in the generated
 * EnumDataAccessPolicyModelResourceSourceType (backend/spec has not added it) —
 * cast at this single usage site so the frontend is forward-compatible
 * without hand-editing src/api/index.ts. RISK: live batch-create/update calls
 * with this value may be rejected until the backend adds CONTENT support.
 */
const RESOURCE_SOURCE_TYPE_CONTENT =
  "CONTENT" as EnumDataAccessPolicyModelResourceSourceType;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Normalises a combinator value from the ConditionBuilder form.
 * The ConditionBuilder stores combinators as CombinatorOption objects
 * ({ id: "AND" | "OR", name: string }), but the API expects a plain string.
 */
function normalizeCombinator(value: unknown): "AND" | "OR" {
  if (typeof value === "string") return value === "AND" ? "AND" : "OR";
  if (typeof value === "object" && value !== null && "id" in value) {
    return (value as { id: string }).id === "AND" ? "AND" : "OR";
  }
  return "OR";
}

/**
 * Maps the attribute's `type` field to the API element type.
 * Defaults to "USER_ATTRIBUTE" for unknown types.
 */
function mapElementType(attributeType: "USER" | "AGENT"): string {
  return attributeType === "AGENT" ? "AGENT_ATTRIBUTE" : "USER_ATTRIBUTE";
}

/**
 * Normalises a condition value to a plain string.
 * When the ConditionBuilder stores an array of {id, name} options (multi-select),
 * the selected ids are joined with a comma.
 */
function normaliseValue(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return (value as Array<{ id?: string } | string>)
      .map(v =>
        typeof v === "object" && v !== null ? (v.id ?? "") : String(v),
      )
      .join(",");
  }
  return String(value);
}

// ─── Core mapper ──────────────────────────────────────────────────────────────

/**
 * Recursively converts a flat ConditionEntry array (from the ConditionBuilder)
 * into the nested DataAccessPolicyRuleColumnsModel[] format expected by the API.
 *
 * Incomplete entries (missing attribute, operator, or value) are silently skipped
 * to prevent server-side validation errors.
 */
export function mapConditionEntriesToColumnRules(
  entries: ConditionEntry[],
  combinator: "AND" | "OR",
): DataAccessPolicyRuleColumnsModel[] {
  const result: DataAccessPolicyRuleColumnsModel[] = [];

  entries.forEach((entry, index) => {
    if (isConditionGroup(entry)) {
      // Nested group — recurse with the group's own combinator
      const nested = mapConditionEntriesToColumnRules(
        entry.conditions,
        normalizeCombinator(entry.combinator),
      );
      if (nested.length === 0) return;
      result.push({
        ruleSequenceNumber: index + 1,
        conjunction: normalizeCombinator(entry.combinator),
        columnRules: nested,
      });
      return;
    }

    // Leaf node — validate completeness before including
    const { attribute, operator, value, matchWith } = entry;
    if (!attribute?.id || !operator?.id) return;
    const normalisedValue = normaliseValue(value);
    if (!normalisedValue) return;

    // The selected Match With option's id IS the API valueType (CONSTANT /
    // CUSTOM_VALUE / USER_ATTRIBUTE / AGENT_ATTRIBUTE). Falls back to CONSTANT
    // for rows with no Match With (no options offered for that attr/operator).
    const valueType = matchWith?.id ?? "CONSTANT";
    // Attribute-picker types compare the LHS against another attribute, so the
    // RHS elementType is the referenced attribute's kind (and `value` holds its
    // name).
    // TODO(TRUST-12340): the attribute-vs-attribute save contract is NOT yet
    // confirmed by the backend. Verify the expected payload shape for
    // USER_ATTRIBUTE / AGENT_ATTRIBUTE match-with rows — specifically `value`
    // (referenced attribute name vs id), `elementType`, and whether a separate
    // `valueName` field is required — before relying on this in production.
    const isAttributePicker =
      valueType === "USER_ATTRIBUTE" || valueType === "AGENT_ATTRIBUTE";

    result.push({
      ruleSequenceNumber: index + 1,
      elementName: attribute.name,
      elementDataType: ELEMENT_DATA_TYPE,
      conditionId: operator.id
        ? (OPERATOR_TO_CONDITION_ID[operator.id] ?? "2")
        : "2",
      selectedConditionValue: operator.name,
      elementType: isAttributePicker
        ? valueType
        : mapElementType(attribute.type as "USER" | "AGENT"),
      valueType,
      value: normalisedValue,
      conjunction: combinator,
      arguments: {},
    });
  });

  return result;
}

/**
 * The condition tree as a readable expression, naming each attribute by the
 * key a saved policy stores (`elementName`, see above) rather than its
 * metadata column id. For every MCP view of a rule expression.
 *
 * Holds for every attribute group — USER, AGENT and RESOURCE options are all
 * built by `toSubAttribute` in `useMcpConditionBuilderConfig` as
 * `{ id: column id, name: stored key, displayName: label }`. (Its
 * `attributePickerOptions` are Match With *value* options, a different shape.)
 */
export const serializeMcpConditionTree = (
  entries: ConditionEntry[],
  combinator: "AND" | "OR" = "OR",
): SerializedGroup =>
  serializeConditionTree(entries, combinator, { attributeKey: "name" });

// ─── Policy payload builders ──────────────────────────────────────────────────

/**
 * Builds a server-level access policy payload.
 * resourceSourceType = "APPLICATION" (controls who can reach this MCP server).
 */
export function buildServerPolicyPayload(
  serverName: string,
  serverRules: ConditionEntry[],
  serverRulesCombinator: "AND" | "OR",
): DataAccessPolicyModel {
  const columnRules = mapConditionEntriesToColumnRules(
    serverRules,
    serverRulesCombinator,
  );

  return {
    name: `mcp_server_access_policy_${serverName.replace(/[^a-zA-Z0-9]/g, "_")}`,
    description: `Server access policy for ${serverName}`,
    policyDataTypeId: POLICY_DATA_TYPE_ID,
    resourceSourceType: EnumDataAccessPolicyModelResourceSourceType.APPLICATION,
    policySubtype: EnumDataAccessPolicyModelPolicySubtype.OBJECT_NAMES,
    rules: [
      {
        queryContextValue: QUERY_CONTEXT_VALUE,
        selectedPrincipalValue: QUERY_CONTEXT_VALUE,
        queryContextId: QUERY_CONTEXT_ID,
        columnRules: [
          {
            ruleSequenceNumber: 1,
            conjunction: serverRulesCombinator,
            columnRules,
          },
        ],
      },
    ],
    evaluationResult: {
      actions: [],
      entitlementsAttributes: [],
      resourceAttributes: [],
      context: {},
    },
  };
}

/**
 * Builds a tool-level access policy payload.
 * resourceSourceType = "RESOURCE" with resourceMetadata identifying the specific tool.
 */
export function buildToolPolicyPayload(
  serverName: string,
  toolName: string,
  toolResourceId: string,
  toolRules: ConditionEntry[],
  toolRulesCombinator: "AND" | "OR",
): DataAccessPolicyModel {
  const columnRules = mapConditionEntriesToColumnRules(
    toolRules,
    toolRulesCombinator,
  );

  return {
    name: `mcp_tool_access_policy_${serverName.replace(/[^a-zA-Z0-9]/g, "_")}_${toolName.replace(/[^a-zA-Z0-9]/g, "_")}`,
    description: `Tool access policy for ${toolName}`,
    policyDataTypeId: POLICY_DATA_TYPE_ID,
    resourceSourceType: EnumDataAccessPolicyModelResourceSourceType.RESOURCE,
    policySubtype: EnumDataAccessPolicyModelPolicySubtype.OBJECT_NAMES,
    resourceMetadata: {
      id: toolResourceId,
      objectType: "Tool",
      objectNames: [toolName],
      resourceIdList: [{ id: toolResourceId, name: toolName }],
    },
    rules: [
      {
        queryContextValue: QUERY_CONTEXT_VALUE,
        selectedPrincipalValue: QUERY_CONTEXT_VALUE,
        queryContextId: QUERY_CONTEXT_ID,
        columnRules: [
          {
            ruleSequenceNumber: 1,
            conjunction: toolRulesCombinator,
            columnRules,
          },
        ],
      },
    ],
    evaluationResult: {
      actions: [],
      entitlementsAttributes: [],
      resourceAttributes: [],
      context: {},
    },
  };
}

/**
 * Deterministic name for a resource policy's payload, keyed on the draft's
 * stable `id` (not its user-editable `name` label) so renaming a resource
 * policy in the UI does not orphan/duplicate it on save — mirrors how
 * buildToolPolicyPayload keys on the stable toolName rather than a
 * user-facing label. Shared between buildResourcePolicyPayload and the
 * idle-preview row synthesis in useRegisterMcpDialogState.ts so both stay
 * in sync.
 */
export function buildResourcePolicyName(
  serverName: string,
  policyId: string,
): string {
  return `mcp_resource_access_policy_${serverName.replace(/[^a-zA-Z0-9]/g, "_")}_${policyId}`;
}

/**
 * Maps each resource-policy draft's deterministic backend `name` (see
 * buildResourcePolicyName) to the user-entered label they typed in the
 * Resource Policies step, so review-panel rows can show a human-readable
 * name instead of the technical `mcp_resource_access_policy_<server>_<id>`
 * key used for backend create/update/matching.
 */
export function buildResourcePolicyDisplayNameMap(
  formValues: Pick<RegisterMcpFormValues, "name" | "resourcePolicies">,
): Map<string, string> {
  const serverName = formValues.name ?? "mcp_server";
  const resourcePolicies = formValues.resourcePolicies ?? [];
  return new Map(
    resourcePolicies.map(policy => [
      buildResourcePolicyName(serverName, policy.id),
      policy.name || "Untitled Resource Policy",
    ]),
  );
}

/**
 * Builds a CONTENT-scoped resource-access policy payload for one
 * ResourcePolicyDraft.
 *
 * Scope encoding: there is no dedicated backend field yet for CONTENT scope
 * (metadata-based vs. a single pinned resource path), so this reuses
 * `resourceMetadata` — a free-form, non-enum-typed object — as a provisional
 * adapter. This is NOT a backend-defined contract and will likely need to
 * change once the backend adds real CONTENT support:
 *   - scope === "specific": resourceMetadata = { id, objectType: "ResourcePath", objectNames: [specificPath] }
 *   - scope === "metadata": resourceMetadata = { id, objectType: "ResourceType", objectNames: resourceTypes }
 */
export function buildResourcePolicyPayload(
  serverName: string,
  policy: ResourcePolicyDraft,
): DataAccessPolicyModel {
  const combinator = normalizeCombinator(policy.combinator);
  const columnRules = mapConditionEntriesToColumnRules(
    policy.conditions ?? [],
    combinator,
  );

  const isSpecific = policy.scope === "specific";
  const specificPath = policy.specificPath?.trim();

  return {
    name: buildResourcePolicyName(serverName, policy.id),
    description: policy.name || "Untitled Resource Policy",
    policyDataTypeId: POLICY_DATA_TYPE_ID,
    resourceSourceType: RESOURCE_SOURCE_TYPE_CONTENT,
    policySubtype: EnumDataAccessPolicyModelPolicySubtype.OBJECT_NAMES,
    resourceMetadata: {
      id: policy.id,
      objectType: isSpecific ? "ResourcePath" : "ResourceType",
      objectNames: isSpecific
        ? specificPath
          ? [specificPath]
          : []
        : (policy.resourceTypes ?? []),
    },
    rules: [
      {
        queryContextValue: QUERY_CONTEXT_VALUE,
        selectedPrincipalValue: QUERY_CONTEXT_VALUE,
        queryContextId: QUERY_CONTEXT_ID,
        columnRules: [
          {
            ruleSequenceNumber: 1,
            conjunction: combinator,
            columnRules,
          },
        ],
      },
    ],
    evaluationResult: {
      actions: [],
      entitlementsAttributes: [],
      resourceAttributes: [],
      context: {},
    },
  };
}

/**
 * Assembles the complete list of policy payloads for a given form state.
 *
 * Always produces the server access policy.
 * Produces a tool policy for each selected tool that has at least one condition
 * and a known resource ID in `toolResourceIdMap`.
 * Produces a resource policy for each draft that clears a minimal validity
 * bar (see the loop below) — resourceSourceType "CONTENT".
 */
export function buildAllPolicyPayloads(
  formValues: RegisterMcpFormValues,
  toolResourceIdMap: Record<string, string>,
): DataAccessPolicyModel[] {
  const policies: DataAccessPolicyModel[] = [];

  // Server policy — always included
  const serverRules = formValues.serverRules ?? [];
  const serverCombinator = normalizeCombinator(
    formValues.serverRulesCombinator,
  );
  policies.push(
    buildServerPolicyPayload(
      formValues.name ?? "mcp_server",
      serverRules,
      serverCombinator,
    ),
  );

  // Tool policies — one per tool that has rules, is selected, has a known resource ID,
  // and is set to 'custom' policy type (tools set to 'inherit' are excluded).
  // Iterates toolRules keys (same source as ReviewAndSaveStep) to keep display and save in sync.
  const toolSelected = formValues.toolSelected ?? {};
  const toolRules = formValues.toolRules ?? {};
  const toolRulesCombinator = formValues.toolRulesCombinator ?? {};
  const toolPolicyTypes = formValues.toolPolicyTypes ?? {};

  for (const toolName of Object.keys(toolRules)) {
    if (!toolName) continue;
    if (!toolSelected[toolName]) continue;
    if ((toolPolicyTypes[toolName] ?? "inherit") === "inherit") continue;

    const rules = toolRules[toolName] ?? [];
    if (rules.length === 0) continue;

    const resourceId = toolResourceIdMap[toolName];
    if (!resourceId) {
      warn(
        `[mapPolicyPayload] No resource ID found for tool "${toolName}" — skipping tool policy.`,
      );
      continue;
    }

    policies.push(
      buildToolPolicyPayload(
        formValues.name ?? "",
        toolName,
        resourceId,
        rules,
        normalizeCombinator(toolRulesCombinator[toolName]),
      ),
    );
  }

  // Resource policies — one per draft that is "valid enough" to save:
  // - metadata scope: needs at least one started condition (an empty
  //   metadata policy would match everything, which is never the intent of
  //   a draft the user hasn't filled in yet).
  // - specific scope: needs a non-empty specificPath OR at least one
  //   started condition.
  const resourcePolicies = formValues.resourcePolicies ?? [];
  for (const policy of resourcePolicies) {
    const isSpecific = policy.scope === "specific";
    const hasPath = !!policy.specificPath?.trim();
    const hasConditions = countResourceConditions(policy.conditions) > 0;
    if (isSpecific ? !hasPath && !hasConditions : !hasConditions) continue;

    policies.push(
      buildResourcePolicyPayload(formValues.name ?? "mcp_server", policy),
    );
  }

  return policies;
}
